import React, { PureComponent } from 'react'
import {
    ConstrainMode, DefaultButton, DetailsListLayoutMode,
    SelectionMode, ShimmeredDetailsList, TextField,
} from '@fluentui/react'
import Export from 'requests/objects/export'
import Status from 'types/status'
import InvalidEntityError from 'requests/errors/invalidEntityError'
import Card from 'components/containers/card'
import CancelRequestError from 'requests/errors/cancelRequestError'
import NotImplementedError from 'requests/errors/notImplementedError'
import UnauthorizedError from 'requests/errors/unauthorizedError'
import { Columns } from 'react-bulma-components'
import Loader from 'components/visuals/loader'
import Time from 'helpers/methods/time'
import TExportType from 'types/exportType'
import TExportStatus from 'types/exportStatus'

/** @debug {import('app').AppProps} */

/**
 * @augments {PureComponent<import('app').AppProps>}
 */
export default class IdExports extends PureComponent {
    constructor(props) {
        super(props)
        this.state = {
            /** @type {Status} Current status of the component */
            status: Status.IDLE,
            /** @type {Export} Element find from API */
            item: new Export(),
            /** @type {import('@fluentui/react').IColumn[]} Columns */
            columns: this.buildColumns(),
        }

        this.cancelExport = this.cancelExport.bind(this)
        this.relaunchExport = this.relaunchExport.bind(this)
    }

    /**
     * @inheritdoc
     */
    componentDidMount() {
        const { setMessageBar } = this.props

        setMessageBar({ isDisplayed: false })

        this.init()
    }

    /**
     * @inheritdoc
     * @param {object} prevProps Previous Props
     */
    componentDidUpdate(prevProps) {
        const { match } = this.props

        if (match?.params?.exportId !== prevProps?.match?.params?.exportId)
            this.init()
    }

    /**
     * @inheritdoc
     */
    componentWillUnmount() {
        this.exportsHandlerGetById?.cancel()
        this.cancelExportHandler?.cancel()
        this.relaunchExportHandler?.cancel()
    }

    /**
     * Setup breadcrumb elements
     */
    setupBreadcrumb() {
        const { setBreadcrumb, match } = this.props

        setBreadcrumb([
            { text: 'Derniers exports', key: 'exports', href: '/last-exports' },
            { text: `Export n°${match?.params?.exportId}`, key: 'one-export', isCurrentItem: true },
        ])
    }

    /**
     * Setup command bar elements
     * @param {number} number num
     * @param {string} suffix suffix
     * @returns {string} Plural
     */
    static get2DigitNumber(number, suffix) {
        const numberString = number < 10 ? `0${number}` : number.toString()
        return numberString + suffix
    }

    /**
     * Get export duration
     * @param {Date} startDate Start date
     * @param {Date} endDate End date
     * @returns {string} Duration
     * @private
     * @static
     * @memberof IdExports
     */
    static getExportDuration(startDate, endDate) {
        if (!startDate)
            return 'N/A'
        const startTime = startDate.getTime()
        const endTime = endDate?.getTime() || new Date().getTime()

        let diff = Math.abs(startTime - endTime)

        const ms = diff % 1000
        diff = (diff - ms) / 1000
        const ss = diff % 60
        diff = (diff - ss) / 60
        const mm = diff % 60
        diff = (diff - mm) / 60
        const hh = diff % 24
        const days = (diff - hh) / 24

        const daysString = days ? this.get2DigitNumber(days, 'j') : null
        const hoursString = hh ? this.get2DigitNumber(hh, 'h') : null
        const minutesString = this.get2DigitNumber(mm, 'm')
        const secondsString = this.get2DigitNumber(ss, 's')
        return [daysString, hoursString, minutesString, secondsString].filter(e => e !== null).join(' ')
    }

    /**
     * Init Page
     */
    init() {
        const {
            setCommand, match, exportHandler, setMessageBar,
        } = this.props

        setCommand([])
        setMessageBar({ isDisplayed: false })
        this.setupBreadcrumb()

        this.setState({ status: Status.PENDING }, async () => {
            try {
                this.exportsHandlerGetById = exportHandler.getById(match?.params?.exportId)
                const exportSolid = await this.exportsHandlerGetById.fetch()
                this.setState({
                    item: exportSolid,
                    status: Status.RESOLVED,
                }, () => {
                    setCommand([])
                })
            } catch (error) {
                switch (error?.constructor) {
                    case CancelRequestError:
                    case UnauthorizedError:
                    case InvalidEntityError: break
                    case NotImplementedError:
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                    default:
                        this.setState({ status: Status.REJECTED })
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                }
            }
        })
    }

    /**
     * Cancel export
     * @param {TExportType} exportType exportType
     */
    cancelExport(exportType) {
        const { exportHandler } = this.props
        const { item } = this.state

        this.setState({ status: Status.PENDING }, async () => {
            try {
                this.cancelExportHandler = exportHandler.cancelExport(item.exportId, exportType)
                const exportSolid = await this.cancelExportHandler.fetch()
                this.setState({
                    item: exportSolid,
                    status: Status.RESOLVED,
                })
            } catch (error) {
                switch (error?.constructor) {
                    case CancelRequestError:
                    case UnauthorizedError:
                    case InvalidEntityError: break
                    case NotImplementedError:
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                    default:
                        this.setState({ status: Status.REJECTED })
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                }
            }
        })
    }

    /**
     * Relaunch export
     * @param {TExportType} exportType exportType
     */
    relaunchExport(exportType) {
        const { exportHandler } = this.props
        const { item } = this.state

        this.setState({ status: Status.PENDING }, async () => {
            try {
                this.relaunchExportHandler = exportHandler.relaunchExport(item.exportId, exportType)
                const exportSolid = await this.relaunchExportHandler.fetch()
                this.setState({
                    item: exportSolid,
                    status: Status.RESOLVED,
                })
            } catch (error) {
                switch (error?.constructor) {
                    case CancelRequestError:
                    case UnauthorizedError:
                    case InvalidEntityError: break
                    case NotImplementedError:
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                    default:
                        this.setState({ status: Status.REJECTED })
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                }
            }
        })
    }

    /**
     * Init <DetailList>
     * @returns {import('@fluentui/react').IColumn[]} Columns
     */
    // eslint-disable-next-line class-methods-use-this
    buildColumns() {
        return [
            {
                key: 'registrationNumber',
                name: 'Matricule',
                fieldName: 'matricule',
                minWidth: 100,
                maxWidth: 200,
                isResizable: true,
            },
            {
                key: 'firstname',
                name: 'Prénom',
                fieldName: 'firstname',
                minWidth: 200,
                maxWidth: 300,
                isResizable: true,
            },
            {
                key: 'lastname',
                name: 'Nom',
                fieldName: 'lastname',
                minWidth: 200,
                maxWidth: 300,
                isResizable: true,
            },
        ]
    }

    /**
     * Render component
     * @returns {JSX.Element} Element
     */
    render() {
        const { item, status, columns } = this.state

        if (status === Status.PENDING)
            return <Loader />

        if (item.collaborators.length === 0)
            return null

        return (
            <main>
                <Card
                    title="Informations"
                    iconName="PageData"
                >
                    <Columns>
                        <Columns.Column size="one-quarter">
                            <TextField
                                label="Société"
                                value={item.company}
                                readOnly
                                borderless
                            />
                        </Columns.Column>
                        <Columns.Column size="one-quarter">
                            <TextField
                                label="Date de début"
                                value={Time(new Date(item.startDate)).getCleanDate({
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                })}
                                readOnly
                                borderless
                            />
                        </Columns.Column>
                        <Columns.Column size="one-quarter">
                            <TextField
                                label="Date de fin"
                                value={Time(new Date(item.endDate)).getCleanDate({
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                })}
                                readOnly
                                borderless
                            />
                        </Columns.Column>
                    </Columns>
                </Card>
                <br />
                <Columns>
                    <Columns.Column size="half">
                        <Card
                            title="Rubriques"
                            iconName="PageList"
                            headerComponent={(
                                <ExportButton
                                    cancelExport={this.cancelExport}
                                    relaunchExport={this.relaunchExport}
                                    exportStatusId={item.rubricExportId}
                                    exportType={TExportType.RUBRICS}
                                    status={status}
                                />
                            )}
                        >
                            <Columns>
                                <Columns.Column size="half">
                                    <TextField
                                        label="Statut"
                                        value={item.rubricExport?.name}
                                        readOnly
                                        borderless
                                    />
                                </Columns.Column>
                                <Columns.Column size="half">
                                    <TextField
                                        label="Temps d'export"
                                        value={IdExports.getExportDuration(item.rubricStartDate, item.rubricEndDate)}
                                        readOnly
                                        borderless
                                    />
                                </Columns.Column>
                            </Columns>
                        </Card>
                    </Columns.Column>
                    <Columns.Column size="half">
                        <Card
                            title="Temps"
                            iconName="PageList"
                            headerComponent={(
                                <ExportButton
                                    cancelExport={this.cancelExport}
                                    relaunchExport={this.relaunchExport}
                                    exportStatusId={item.timeExportId}
                                    exportType={TExportType.TIMES}
                                    status={status}
                                />
                            )}
                        >
                            <Columns>
                                <Columns.Column size="half">
                                    <TextField
                                        label="Statut"
                                        value={item.timeExport?.name}
                                        readOnly
                                        borderless
                                    />
                                </Columns.Column>
                                <Columns.Column size="half">
                                    <TextField
                                        label="Temps d'export"
                                        value={IdExports.getExportDuration(item.timeStartDate, item.timeEndDate)}
                                        readOnly
                                        borderless
                                    />
                                </Columns.Column>
                            </Columns>
                        </Card>
                    </Columns.Column>
                </Columns>
                <Card
                    title="Collaborateurs"
                    iconName="People"
                    hasDividerMargin={false}
                >
                    <ShimmeredDetailsList
                        items={item.collaborators}
                        columns={columns}
                        selectionMode={SelectionMode.none}
                        onShouldVirtualize={() => true}
                        enableShimmer={status === Status.PENDING}
                        layoutMode={DetailsListLayoutMode.justified}
                        constrainMode={ConstrainMode.unconstrained}
                    />
                </Card>
            </main>
        )
    }
}

/**
 * Export button
 * @param {object} props Props
 * @param {number} props.exportStatusId Export status
 * @param {TExportType} props.exportType Export type
 * @param {(arg0: TExportType) => void} props.cancelExport Cancel export callback
 * @param {(arg0: TExportType) => void} props.relaunchExport Relaunch export callback
 * @param {Status} props.status Status
 * @returns {JSX.Element} Element
 */
function ExportButton({
    exportStatusId, exportType, cancelExport, relaunchExport, status,
}) {
    if ([TExportStatus.RESOLVED, TExportStatus.UNDEFINED].includes(exportStatusId))
        return (<div style={{ height: '32px' }} />)
    if ([TExportStatus.IN_PROGRESS, TExportStatus.WAITING].includes(exportStatusId))
        return (
            <DefaultButton
                text="Annuler"
                iconProps={{
                    iconName: 'Cancel',
                    styles: { root: { color: '#a4262c' } },
                }}
                title={`Annuler l'export des ${exportType === 'rubric' ? 'rubriques' : 'temps'}`}
                onClick={() => {
                    cancelExport(exportType)
                }}
                disabled={status === Status.PENDING}
            />
        )

    return (
        <DefaultButton
            text="Relancer"
            iconProps={{
                iconName: 'Refresh',
                styles: { root: { color: '#164080' } },
            }}
            title={`Relancer l'export des ${exportType === 'rubric' ? 'rubriques' : 'temps'}`}
            onClick={() => {
                relaunchExport(exportType)
            }}
            disabled={status === Status.PENDING}
        />
    )
}
